import { useEffect, useRef, useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Ellipsis,
  ImageIcon,
  Music2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Spinner } from "@/components/ui/spinner"
import { videoClient } from "@/lib/video-client"

export const Route = createFileRoute("/app/medias")({
  head: () => ({
    meta: [
      { title: "Media | intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MediaPage,
})

type Asset = Awaited<ReturnType<typeof videoClient.media.list>>[number]
const accepted =
  ".jpg,.jpeg,.png,.webp,.mp3,.wav,image/jpeg,image/png,image/webp,audio/mpeg,audio/wav"

function useDebounce<T>(value: T, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay)
    return () => window.clearTimeout(timeout)
  }, [value, delay])
  return debouncedValue
}

function canonicalMedia(file: File) {
  if (["image/jpeg", "image/png", "image/webp"].includes(file.type))
    return {
      contentType: file.type as "image/jpeg" | "image/png" | "image/webp",
      maximum: 25 * 1024 * 1024,
    }
  const extension = file.name.split(".").at(-1)?.toLowerCase()
  if (
    extension === "mp3" &&
    (!file.type || ["audio/mpeg", "audio/mp3"].includes(file.type))
  )
    return { contentType: "audio/mpeg" as const, maximum: 25 * 1024 * 1024 }
  if (
    extension === "wav" &&
    (!file.type ||
      ["audio/wav", "audio/x-wav", "audio/wave"].includes(file.type))
  )
    return { contentType: "audio/wav" as const, maximum: 25 * 1024 * 1024 }
  return null
}

function MediaPage() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(0)
  const [search, setSearch] = useState("")
  const [renameTarget, setRenameTarget] = useState<Asset | null>(null)
  const [renameFilename, setRenameFilename] = useState("")
  const [renaming, setRenaming] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Asset | null>(null)
  const [deleting, setDeleting] = useState(false)
  const debouncedSearch = useDebounce(search)

  async function load() {
    try {
      setAssets(await videoClient.media.list())
    } catch {
      toast.error("We could not load your media library. Try again.")
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
  }, [])

  const normalizedSearch = debouncedSearch.trim().toLowerCase()
  const filteredAssets = normalizedSearch
    ? assets.filter((asset) =>
        asset.filename.toLowerCase().includes(normalizedSearch)
      )
    : assets
  const images = filteredAssets.filter((asset) => asset.kind === "image")
  const audio = filteredAssets.filter((asset) => asset.kind === "audio")

  async function renameAsset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const filename = renameFilename.trim()
    if (!renameTarget || !filename) return
    setRenaming(true)
    try {
      const renamed = await videoClient.media.rename({
        assetId: renameTarget.id,
        filename,
      })
      setAssets((current) =>
        current.map((asset) => (asset.id === renamed.id ? renamed : asset))
      )
      setRenameTarget(null)
      toast.success("Media renamed.")
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "We could not rename this media file. Try again."
      )
    } finally {
      setRenaming(false)
    }
  }

  async function deleteAsset() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await videoClient.media.delete({ assetId: deleteTarget.id })
      setAssets((current) =>
        current.filter((asset) => asset.id !== deleteTarget.id)
      )
      setDeleteTarget(null)
      toast.success("Media deleted permanently.")
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "We could not delete this media file. Try again."
      )
    } finally {
      setDeleting(false)
    }
  }

  function renderAsset(asset: Asset) {
    return (
      <article className="media-library-asset" key={asset.id}>
        <div className="media-library-preview">
          {asset.kind === "image" ? (
            <img src={asset.media.url} alt="" />
          ) : (
            <Music2 aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{asset.filename}</p>
          <p className="text-sm text-muted-foreground">
            {asset.kind === "image" ? (
              <>
                <ImageIcon className="inline size-4" /> Image
              </>
            ) : (
              "Audio"
            )}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="ghost" size="icon-sm" />}
            aria-label={`Actions for ${asset.filename}`}
          >
            <Ellipsis aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  setRenameFilename(asset.filename)
                  setRenameTarget(asset)
                }}
              >
                <Pencil aria-hidden="true" /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteTarget(asset)}
              >
                <Trash2 aria-hidden="true" /> Delete
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </article>
    )
  }

  async function upload(file: File) {
    const validated = canonicalMedia(file)
    if (!validated) {
      toast.error(`${file.name} must be a JPEG, PNG, WebP, MP3, or WAV file.`)
      return
    }
    if (file.size > validated.maximum) {
      toast.error(`${file.name} is larger than the media size limit.`)
      return
    }
    setUploading((value) => value + 1)
    let assetId: string | null = null
    try {
      const pending = await videoClient.media.createUpload({
        filename: file.name,
        contentType: validated.contentType,
        sizeBytes: file.size,
      })
      assetId = pending.assetId
      const response = await fetch(pending.uploadUrl, {
        method: "PUT",
        headers: pending.uploadHeaders,
        body: file,
      })
      if (!response.ok) throw new Error("upload")
      const asset = await videoClient.media.completeUpload({ assetId })
      assetId = null
      setAssets((current) => [asset, ...current])
      toast.success(`${file.name} was added to your media library.`)
    } catch (error) {
      const message = error instanceof Error ? error.message : ""
      toast.error(
        message.includes("already exists")
          ? "A media file with this name already exists in your library."
          : `We could not upload ${file.name}. Try again.`
      )
    } finally {
      if (assetId) void videoClient.media.abandonUpload({ assetId })
      setUploading((value) => Math.max(0, value - 1))
    }
  }

  return (
    <main className="app-main" aria-labelledby="media-title">
      <section className="media-library-page">
        <div className="media-library-heading">
          <div>
            <h1 id="media-title" className="text-3xl font-semibold">
              Media
            </h1>
            <p className="text-muted-foreground">
              Import media once, then reuse it in any short.
            </p>
          </div>
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading > 0}
          >
            {uploading > 0 ? (
              <Spinner aria-hidden="true" />
            ) : (
              <Plus aria-hidden="true" />
            )}{" "}
            Import media
          </Button>
          <Input
            ref={inputRef}
            className="sr-only size-px p-0"
            type="file"
            accept={accepted}
            multiple
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              for (const file of Array.from(event.target.files ?? []))
                void upload(file)
              event.target.value = ""
            }}
          />
        </div>
        {assets.length > 0 && (
          <InputGroup className="mb-4" role="search">
            <InputGroupAddon align="inline-start">
              <Search aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search media by file name"
              placeholder="Search media by file name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </InputGroup>
        )}
        <Card>
          <CardContent className="lg:px-0">
            {loading ? (
              <div className="flex items-center gap-2" role="status">
                <Spinner /> Loading media
              </div>
            ) : assets.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Plus />
                  </EmptyMedia>
                  <EmptyTitle>No media yet</EmptyTitle>
                  <EmptyDescription>
                    Import an image or audio file to reuse it across your
                    shorts.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : filteredAssets.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>No media found</EmptyTitle>
                  <EmptyDescription>
                    Try searching for a different file name.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <div className="flex flex-col gap-6">
                <section aria-labelledby="images-title">
                  <h2 id="images-title" className="mb-3 text-lg font-medium">
                    Images
                  </h2>
                  {images.length > 0 ? (
                    <div className="media-library-grid">
                      {images.map(renderAsset)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No images found.
                    </p>
                  )}
                </section>
                <section aria-labelledby="audio-title">
                  <h2 id="audio-title" className="mb-3 text-lg font-medium">
                    Audio
                  </h2>
                  {audio.length > 0 ? (
                    <div className="media-library-grid">
                      {audio.map(renderAsset)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No audio found.
                    </p>
                  )}
                </section>
              </div>
            )}
          </CardContent>
        </Card>
        <Dialog
          open={renameTarget !== null}
          onOpenChange={(open) => {
            if (!open && !renaming) setRenameTarget(null)
          }}
        >
          <DialogContent showCloseButton={!renaming}>
            <DialogHeader>
              <DialogTitle>Rename media</DialogTitle>
              <DialogDescription>
                Change the file name shown in your media library.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={renameAsset} aria-busy={renaming}>
              <FieldGroup>
                <Field data-disabled={renaming}>
                  <FieldLabel htmlFor="media-filename">File name</FieldLabel>
                  <Input
                    id="media-filename"
                    required
                    maxLength={255}
                    disabled={renaming}
                    value={renameFilename}
                    onChange={(event) => setRenameFilename(event.target.value)}
                  />
                </Field>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={renaming}
                    onClick={() => setRenameTarget(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={renaming || !renameFilename.trim()}
                  >
                    {renaming && <Spinner data-icon="inline-start" />}{" "}
                    {renaming ? "Renaming..." : "Rename"}
                  </Button>
                </DialogFooter>
              </FieldGroup>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open && !deleting) setDeleteTarget(null)
          }}
        >
          <DialogContent showCloseButton={!deleting}>
            <DialogHeader>
              <DialogTitle>Delete media permanently?</DialogTitle>
              <DialogDescription>
                This permanently deletes &quot;{deleteTarget?.filename}&quot;
                from your media library and storage. Media used by a short
                cannot be deleted. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={deleting}
                onClick={() => void deleteAsset()}
              >
                {deleting && <Spinner data-icon="inline-start" />}{" "}
                {deleting ? "Deleting..." : "Delete permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </main>
  )
}
